import React, { useEffect, useState } from 'react';
import {
  Box, Paper, CircularProgress, Typography,
  Snackbar, Alert, Checkbox, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Button
} from '@mui/material';
import ReusableTable from '../../../components/common/ui/ReusableTable';
import { PiWarningCircleBold } from 'react-icons/pi';
import { H2, H3TableHeader } from '../../../styled-components/Typography';
import { useDatabase } from '../../../contexts/DatabaseContext';
import { PrimaryButton } from '../../../styled-components/Buttons';
import { useNavigate } from 'react-router-dom';

const DatabaseList = () => {
  const { fetchUserCredentials } = useDatabase();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false)
  const navigate = useNavigate();

  useEffect(() => {
    async function loadCredentials() {
      try {
        if (credentials.length === 0 && isLoaded == false) {
          const data = await fetchUserCredentials();
          setCredentials(data);
          setIsLoaded(true)
        }
      } catch (err) {
        setError(err.message);
        setOpenAlert(true);
      } finally {
        setLoading(false);
      }
    }
    loadCredentials();
  }, [credentials]);

  const handleCloseAlert = () => setOpenAlert(false);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(credentials.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    try {
      const res = await fetch('https://postsheetapp.com/api/credentials/delete/', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (!res.ok) throw new Error('Fail deleting');
      setCredentials(prev => prev.filter(c => !selectedIds.includes(c.id)));
      setSelectedIds([]);
      setOpenDeleteModal(false);
    } catch (err) {
      setError(err.message);
      setOpenAlert(true);
    }
  };

  const baseColumns = [
    { title: 'Database Name', field: 'alias', align: 'left' },
    { title: 'Database Type', field: 'database_type', align: 'left' },
  ];

  const columnsWithSelection = [
    {
      title: (
        <Checkbox
          indeterminate={
            selectedIds.length > 0 &&
            selectedIds.length < credentials.length
          }
          checked={
            credentials.length > 0 &&
            selectedIds.length === credentials.length
          }
          onChange={handleSelectAll}
        />
      ),
      field: 'id',
      render: rowData => (
        <Checkbox
          checked={selectedIds.includes(rowData.id)}
          onChange={() => handleSelectOne(rowData.id)}
        />
      ),
    },
    ...baseColumns
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Snackbar open={openAlert} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Paper style={{ width: '100%', padding: '20px' }}>
        <Box style={{ width: '100%' }}>
          <H2>My Databases</H2>
          <br /><br />

          {loading ? (
            <Paper style={{ padding: 2, marginBottom: 3 }}>
              <H3TableHeader>My Databases</H3TableHeader>
              <Box display="flex" justifyContent="center" alignItems="center" height={100}>
                <CircularProgress />
              </Box>
            </Paper>
          ) : credentials.length > 0 ? (
            <Paper style={{ padding: 2, marginBottom: 3 }}>
              <ReusableTable
                data={credentials}
                columns={columnsWithSelection}
                noDataText="No databases found."
              />
            </Paper>
          ) : (
            <Paper style={{ padding: 2, marginBottom: 3 }}>
              <Box display="flex" alignItems="center">
                <PiWarningCircleBold size={18} />
                <Typography style={{ marginLeft: '0.75rem', lineHeight: '2.5rem' }}>
                  No databases found.
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, margin: '2rem' }}>
          <PrimaryButton onClick={() => navigate('/app/database/add')}>
            Add
          </PrimaryButton>
          <PrimaryButton
            color="secondary"
            disabled={selectedIds.length === 0}
            onClick={() => setOpenDeleteModal(true)}
          >
            Delete
          </PrimaryButton>
        </Box>
      </Paper>

      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the selected database(s)?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
          <Button onClick={handleDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatabaseList;
